import { createTheme, ThemeOptions } from '@mui/material/styles';
import {
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_100,
  COLOR_PRIMARY_2,
  COLOR_SECONDARY_1,
  COLOR_TEXT_1,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
  COLOR_TEXT_4,
} from 'src/colors';

// Spacing
export const spacing = {
  section: 24,
  subsection: 16,
  verticalGap: 16,
  xPadding: 20,
  yPadding: 24,
  cardPadding: 16,
  cardGap: 16,
};

// Typography sizes
export const fontSizes = {
  heading: 30,
  subHeading: 20,
  subHeading2: 18,
  body1: 16,
  body2: 14,
  tabItem: 18,
};

// Typography colors
export const textColors = {
  heading: COLOR_TEXT_1,
  subHeading: COLOR_TEXT_2,
  tabItem: COLOR_TEXT_3,
  body: COLOR_TEXT_4,
};

const theme = createTheme({
  spacing: 8, // base unit (used with multiples: theme.spacing(2) = 16px)
  palette: {
    primary: {
      main: COLOR_PRIMARY_1,
      light: COLOR_PRIMARY_1_100,
      dark: COLOR_PRIMARY_2,
    },
    secondary: {
      main: COLOR_SECONDARY_1,
    },
    text: {
      primary: COLOR_TEXT_1,
      secondary: COLOR_TEXT_2,
      disabled: COLOR_TEXT_3,
    },
    neutral: {
      main: '#64748B',
      contrastText: '#fff',
    },
  },
  typography: {
    fontFamily: `'Roboto', 'Helvetica', 'Arial', sans-serif`,
    h1: {
      fontSize: `${fontSizes.heading}px`,
      fontWeight: 600,
      color: textColors.heading,
    },
    h2: {
      fontSize: `${fontSizes.subHeading}px`,
      fontWeight: 500,
      color: textColors.subHeading,
    },
    h3: {
      fontSize: `${fontSizes.subHeading2}px`,
      fontWeight: 500,
      color: textColors.subHeading,
    },
    body1: {
      fontSize: `${fontSizes.body1}px`,
      color: textColors.body,
    },
    body2: {
      fontSize: `${fontSizes.body2}px`,
      color: textColors.body,
    },
    button: {
      textTransform: 'none',
    },
  },
  components: {
    // MuiTab: {
    //   styleOverrides: {
    //     root: {
    //       fontSize: `${fontSizes.tabItem}px`,
    //       color: textColors.tabItem,
    //       textTransform: 'none',
    //     },
    //   },
    // },
    MuiCard: {
      styleOverrides: {
        root: {
          padding: `${spacing.yPadding}px ${spacing.xPadding}px`,
        },
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: 'lg',
      },
      styleOverrides: {
        root: {
          paddingLeft: spacing.xPadding,
          paddingRight: spacing.xPadding,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          padding: '0px',
          minHeight: '48px',
          paddingBottom: '4px',
          marginBottom: '8px',
          height: 'auto',
          borderBottom: '1px solid #e0e0e0', // subtle divider
        },
        scroller: {
          minHeight: 0,
          height: 'auto',
        },
        flexContainer: {
          minHeight: 0,
          height: 'auto',
          gap: '24px', // adds spacing between tabs
        },
        indicator: {
          height: '3px',
          bottom: 0,
          borderRadius: '3px 3px 0 0',
          backgroundColor: COLOR_PRIMARY_1,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: `${fontSizes.tabItem}px`,
          fontWeight: 500,
          color: '#555',
          minHeight: '48px',
          padding: '6px 16px',
          '&.Mui-selected': {
            color: COLOR_PRIMARY_2,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        .rightView h1 {
          font-size: ${fontSizes.heading}px;
          color: ${textColors.heading};
        }
        .rightView h2 {
          font-size: ${fontSizes.subHeading}px;
          color: ${textColors.subHeading};
        }
        .rightView .MuiTab-root {
          font-size: ${fontSizes.tabItem}px;
          color: ${textColors.tabItem};
        }
        .rightView .MuiTypography-body1 {
          color: ${textColors.body};
        }
        .MuiCard-root.padded {
          padding: ${spacing.yPadding}px ${spacing.xPadding}px;
        }
        .section--spacing {
          margin-bottom: ${spacing.section}px !important;
        }
        .subsection--spacing {
          margin-bottom: ${spacing.subsection}px !important;
        }
      `,
    },
  },
  utils: {
    container: {
      maxWidth: '900px',
      margin: '0 auto',
    },
    spacing: {
      section: '20px',
      subsection: '16px',
      halfSubsection: '8px',
    },
    cardPadding: {
      padding: `${spacing.cardPadding}px`,
      boxSizing: 'border-box',
    },
    cardGap: {
      gap: `${spacing.cardGap}px`,
    },
  },
} as ThemeOptions);

export default theme;
